/**
 * Merges Biosure portal branding (lab → central → env defaults from API) into Logto
 * sign-in experience settings.
 *
 * Discovery (first match): `window.__PORTAL_API_BASE` → meta `portal-api-base` →
 * `import.meta.env.VITE_PORTAL_PUBLIC_API_URL` (build-time).
 * When a base URL is resolved, the experience POSTs to `/api/v1/platform/branding` (always when configured).
 *
 * Logo/favicon URLs are HTTPS (e.g. S3 presigned); the browser loads them normally.
 * Ensure Logto CSP `connect-src` allows your portal API origin (set EXPERIENCE_EXTRA_CONNECT_SRC on the
 * Logto core process when NODE_ENV=production, e.g. Docker). Relax `img-src` if you use a strict Next CSP for assets.
 */
import { isObject } from '@silverhand/essentials';

import { searchKeys } from '@/shared/utils/search-parameters';
import type { SignInExperienceResponse } from '@/types';

import { applyPortalBrandingToCssVariables, type PortalBranding } from './branding-map';

function normalizeBase(url: string): string {
  return url.replace(/\/+$/, '');
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

function mapRecordToPortalBranding(data: Record<string, unknown>): PortalBranding {
  const readString = (key: string): string | undefined => {
    const value: unknown = Reflect.get(data, key);
    if (value === null || value === undefined) {
      return undefined;
    }
    return typeof value === 'string' ? value : undefined;
  };

  return {
    brandName: readString('portalName'),
    logoUrl: readString('logoUrl'),
    logoDarkUrl: readString('logoDarkUrl'),
    faviconUrl: readString('faviconUrl'),
    primaryColor: readString('primaryColor'),
    secondaryColor: undefined,
    backgroundColor: undefined,
    textColor: undefined,
    partnerPoweredByLabel: readString('partnerPoweredByLabel'),
    partnerLogoUrl: readString('partnerLogoUrl'),
    partnerDarkLogoUrl: readString('partnerDarkLogoUrl'),
    partnerWebsiteUrl: readString('partnerWebsiteUrl'),
  };
}

/**
 * Resolves the portal API origin for `/api/v1/platform/branding`.
 * Override at runtime: `window.__PORTAL_API_BASE`, or
 * `<meta name="portal-api-base" content="https://...">`, or build with `VITE_PORTAL_PUBLIC_API_URL`.
 */
export function getDiscoveryBaseUrl(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const fromGlobal = (window.__PORTAL_API_BASE ?? '').trim();
  if (fromGlobal) {
    return normalizeBase(fromGlobal);
  }

  try {
    const meta = document.querySelector('meta[name="portal-api-base"]')?.getAttribute('content');
    const fromMeta = (meta ?? '').trim();
    if (fromMeta) {
      return normalizeBase(fromMeta);
    }
  } catch {
    // ignore
  }

  const viteUrl = import.meta.env.VITE_PORTAL_PUBLIC_API_URL
    ? String(import.meta.env.VITE_PORTAL_PUBLIC_API_URL).trim()
    : '';
  return viteUrl ? normalizeBase(viteUrl) : '';
}

/** @deprecated Prefer getDiscoveryBaseUrl */
export function getPortalApiBaseUrl(): string {
  return getDiscoveryBaseUrl();
}

/**
 * Resolves after Logto moves `organization_id` from the URL into sessionStorage (see search-parameters).
 */
export function getLogtoOrganizationIdOrNull(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const url = new URL(window.location.href);
    const fromQuery =
      url.searchParams.get(searchKeys.organizationId) ?? url.searchParams.get('organizationId');
    if (fromQuery) {
      return fromQuery.trim();
    }
  } catch {
    // ignore
  }
  const fromStorage = sessionStorage.getItem(searchKeys.organizationId);
  const trimmed = fromStorage?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Fetches effective portal branding: with `organization_id` → lab (if allowed) over platform;
 * without it → central platform branding (e.g. super-admin sign-in).
 */
export async function fetchPortalWhiteLabelBranding(): Promise<PortalBranding | undefined> {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const base = getDiscoveryBaseUrl();
  if (!base) {
    return undefined;
  }

  const organizationId = getLogtoOrganizationIdOrNull();
  const body = organizationId ? { action: 'resolveByOrganizationId', organizationId } : {};

  try {
    const response = await fetch(`${base}/api/v1/platform/branding`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json: unknown = await response.json().catch(() => ({}));
    if (!isObject(json)) {
      return undefined;
    }
    const success: unknown = Reflect.get(json, 'success');
    if (success !== true) {
      return undefined;
    }
    const payload: unknown = Reflect.get(json, 'data');
    if (!isRecord(payload)) {
      return undefined;
    }
    return mapRecordToPortalBranding(payload);
  } catch {
    return undefined;
  }
}

const hexColorRe = /^#([\da-f]{3}|[\da-f]{6})$/i;

export function mergePortalWhiteLabelIntoSignInExperience(
  settings: SignInExperienceResponse,
  portal: PortalBranding | undefined
): SignInExperienceResponse {
  if (!portal) {
    return settings;
  }

  const logoUrlTrimmed = portal.logoUrl?.trim() ?? '';
  const darkLogoTrimmed = portal.logoDarkUrl?.trim() ?? '';
  const faviconTrimmed = portal.faviconUrl?.trim() ?? '';

  const hasLogo = logoUrlTrimmed.length > 0;
  const hasDarkLogo = darkLogoTrimmed.length > 0;
  const hasFavicon = faviconTrimmed.length > 0;

  const withLogo = hasLogo
    ? { ...settings.branding, logoUrl: logoUrlTrimmed }
    : { ...settings.branding };

  const withDark = hasDarkLogo
    ? { ...withLogo, darkLogoUrl: darkLogoTrimmed }
    : hasLogo
      ? { ...withLogo, darkLogoUrl: logoUrlTrimmed }
      : withLogo;

  const withFavicon = hasFavicon
    ? { ...withDark, favicon: faviconTrimmed, darkFavicon: faviconTrimmed }
    : withDark;

  const pc = portal.primaryColor?.trim();
  const nextColor =
    pc && hexColorRe.test(pc) ? { ...settings.color, primaryColor: pc } : settings.color;

  const pLabel = portal.partnerPoweredByLabel?.trim();
  const pSite = portal.partnerWebsiteUrl?.trim();
  const pLogo = portal.partnerLogoUrl?.trim();
  const pDarkLogo = portal.partnerDarkLogoUrl?.trim();

  const finalBranding = {
    ...withFavicon,
    ...(pLabel && pLabel.length > 0 ? { partnerPoweredByLabel: pLabel } : {}),
    ...(pSite && pSite.length > 0 ? { partnerWebsiteUrl: pSite } : {}),
    ...(pLogo && pLogo.length > 0 ? { partnerLogoUrl: pLogo } : {}),
    ...(pDarkLogo && pDarkLogo.length > 0 ? { partnerDarkLogoUrl: pDarkLogo } : {}),
  };

  return { ...settings, branding: finalBranding, color: nextColor };
}

export async function applyPortalWhiteLabelAndCssVariables(
  settings: SignInExperienceResponse
): Promise<SignInExperienceResponse> {
  const portal = await fetchPortalWhiteLabelBranding();
  const merged = mergePortalWhiteLabelIntoSignInExperience(settings, portal);
  if (portal) {
    applyPortalBrandingToCssVariables(portal);
  }
  return merged;
}
